/**
 * Admin Panel Page - Traklet Frontend
 * Purpose: Admin-only page for viewing registered users and system management
 * Author: Traklet App
 * Created: 2025
 * Features: User management, registration alerts, system statistics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Badge,
  Snackbar,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  Stack,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Link as LinkIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  NotificationsActive as NotificationsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { logger } from '../utils/config';
import { adminApi } from '../utils/api';

// Types
interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: number;
  created_at: string;
  updated_at: string;
  url_count: number;
  total_clicks: number;
}

interface DashboardStats {
  users: {
    total_users: number;
    active_users: number;
    admin_users: number;
    new_users_30d: number;
  };
  urls: {
    total_urls: number;
    active_urls: number;
    new_urls_30d: number;
    avg_clicks_per_url: number;
  };
  clicks: {
    total_clicks: number;
    clicks_24h: number;
    clicks_7d: number;
    clicks_30d: number;
  };
  system: {
    database_size: string;
    memory_usage: string;
    php_version: string;
  };
}

interface RecentRegistration {
  id: number;
  username: string;
  email: string;
  created_at: string;
}

const Admin: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentRegistrations, setRecentRegistrations] = useState<RecentRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({ open: false, title: '', message: '', action: () => {} });

  // Check if user is admin
  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Fetch dashboard statistics
  const fetchStats = async () => {
    try {
      const response = await adminApi.getDashboardStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      logger.error('Failed to fetch dashboard stats:', error);
    }
  };

  // Fetch users with filters
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter })
      };

      const response = await adminApi.getUsers(params);
      if (response.success) {
        setUsers(response.data.users);
        setTotalUsers(response.data.pagination.total);
      }
    } catch (error) {
      logger.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch recent registrations for alerts
  const fetchRecentRegistrations = async () => {
    try {
      const response = await adminApi.getRecentRegistrations(24);
      if (response.success) {
        setRecentRegistrations(response.data);
        if (response.data.length > 0) {
          setAlertMessage(`${response.data.length} new user(s) registered in the last 24 hours`);
          setAlertOpen(true);
        }
      }
    } catch (error) {
      logger.error('Failed to fetch recent registrations:', error);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchUsers(),
        fetchRecentRegistrations()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Refetch users when filters change
  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter]);

  // Auto-refresh recent registrations every 5 minutes
  useEffect(() => {
    const interval = setInterval(fetchRecentRegistrations, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchStats();
    fetchUsers();
    fetchRecentRegistrations();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleUserAction = async (userId: number, action: 'activate' | 'deactivate' | 'delete') => {
    try {
      let response;
      switch (action) {
        case 'activate':
          response = await adminApi.updateUserStatus(userId, true);
          break;
        case 'deactivate':
          response = await adminApi.updateUserStatus(userId, false);
          break;
        case 'delete':
          response = await adminApi.deleteUser(userId);
          break;
      }
      
      if (response.success) {
        setAlertMessage(`User ${action}d successfully`);
        setAlertOpen(true);
        fetchUsers(); // Refresh the users list
      }
    } catch (error) {
      logger.error(`Failed to ${action} user:`, error);
      setAlertMessage(`Failed to ${action} user`);
      setAlertOpen(true);
    }
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const openConfirmDialog = (title: string, message: string, action: () => void) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={{ xs: 3, md: 4 }}
      >
        <Box>
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h1" 
            fontWeight={600}
            color="text.primary"
            sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' } }}
          >
            Admin Panel
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Manage users and monitor system performance
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton 
            onClick={handleRefresh} 
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 }
            }}
          >
            <RefreshIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Registration Alerts */}
      {recentRegistrations.length > 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            mb: 3, 
            p: { xs: 2, sm: 3 },
            border: '1px solid',
            borderColor: 'info.light',
            borderRadius: 2,
            bgcolor: 'info.50'
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <NotificationsIcon color="info" fontSize="small" />
            <Typography 
              variant="subtitle1" 
              fontWeight={600}
              color="info.main"
            >
              Recent Registrations
            </Typography>
          </Stack>
          <Typography 
            variant="body2" 
            color="text.secondary"
            mb={2}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {recentRegistrations.length} new user(s) registered in the last 24 hours
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {recentRegistrations.slice(0, 3).map((reg) => (
              <Chip
                key={reg.id}
                label={`${reg.username} (${new Date(reg.created_at).toLocaleDateString()})`}
                size={isMobile ? "small" : "medium"}
                color="info"
                variant="outlined"
              />
            ))}
            {recentRegistrations.length > 3 && (
              <Chip
                label={`+${recentRegistrations.length - 3} more`}
                size={isMobile ? "small" : "medium"}
                color="info"
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Dashboard Statistics */}
      {stats && (
        <Grid container spacing={{ xs: 2, sm: 3 }} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 1
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <PeopleIcon color="primary" fontSize={isMobile ? 'medium' : 'large'} />
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight={500}
                    color="text.primary"
                  >
                    Users
                  </Typography>
                </Stack>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  fontWeight={700}
                  color="primary.main"
                  sx={{ mb: 1 }}
                >
                  {stats.users.total_users.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  +{stats.users.new_users_30d} this month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 1
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <LinkIcon color="primary" fontSize={isMobile ? 'medium' : 'large'} />
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight={500}
                    color="text.primary"
                  >
                    URLs
                  </Typography>
                </Stack>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  fontWeight={700}
                  color="primary.main"
                  sx={{ mb: 1 }}
                >
                  {stats.urls.total_urls.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  +{stats.urls.new_urls_30d} this month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 1
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AnalyticsIcon color="primary" fontSize={isMobile ? 'medium' : 'large'} />
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight={500}
                    color="text.primary"
                  >
                    Clicks
                  </Typography>
                </Stack>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  fontWeight={700}
                  color="primary.main"
                  sx={{ mb: 1 }}
                >
                  {stats.clicks.total_clicks.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {stats.clicks.clicks_24h} in 24h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 1
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight={500}
                  color="text.primary"
                  mb={2}
                >
                  System Health
                </Typography>
                <Stack spacing={1}>
                  <Typography 
                    variant="body2" 
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    <strong>DB:</strong> {stats.system.database_size}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    <strong>Memory:</strong> {stats.system.memory_usage}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    <strong>PHP:</strong> {stats.system.php_version}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Users Management */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          mb={{ xs: 2, sm: 3 }}
          fontWeight={600}
          color="text.primary"
        >
          User Management
        </Typography>
        
        {/* Filters */}
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={{ xs: 2, md: 3 }} 
          mb={3}
        >
          <TextField
            fullWidth
            label="Search users"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              flex: { md: 2 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <FormControl sx={{ flex: 1, minWidth: 120 }}>
            <InputLabel size={isMobile ? "small" : "normal"}>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              size={isMobile ? "small" : "medium"}
              onChange={(e) => setRoleFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ flex: 1, minWidth: 120 }}>
            <InputLabel size={isMobile ? "small" : "normal"}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              size={isMobile ? "small" : "medium"}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Users List - Responsive */}
        {isMobile ? (
          // Mobile Card Layout
          <Stack spacing={2}>
            {usersLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              users.map((user) => (
                <Card 
                  key={user.id} 
                  elevation={0}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 1
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      {/* User Header */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {user.id} • {user.email}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip 
                            label={user.role} 
                            color={user.role === 'admin' ? 'primary' : 'default'}
                            size="small"
                          />
                          <Chip 
                            label={user.is_active ? 'Active' : 'Inactive'} 
                            color={user.is_active ? 'success' : 'error'}
                            size="small"
                          />
                        </Stack>
                      </Stack>
                      
                      {/* User Stats */}
                      <Stack direction="row" spacing={3}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            URLs
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {(user.url_count || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Clicks
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {(user.total_clicks || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Registered
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(user.created_at)}
                          </Typography>
                        </Box>
                      </Stack>
                      
                      {/* Actions */}
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {user.is_active ? (
                          <Button 
                            size="small" 
                            variant="outlined"
                            color="warning"
                            startIcon={<BlockIcon />}
                            onClick={() => openConfirmDialog(
                              'Deactivate User',
                              `Are you sure you want to deactivate ${user.username}?`,
                              () => handleUserAction(user.id, 'deactivate')
                            )}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button 
                            size="small" 
                            variant="outlined"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => openConfirmDialog(
                              'Activate User',
                              `Are you sure you want to activate ${user.username}?`,
                              () => handleUserAction(user.id, 'activate')
                            )}
                          >
                            Activate
                          </Button>
                        )}
                        {user.role !== 'admin' && (
                          <Button 
                            size="small" 
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => openConfirmDialog(
                              'Delete User',
                              `Are you sure you want to permanently delete ${user.username}? This action cannot be undone.`,
                              () => handleUserAction(user.id, 'delete')
                            )}
                          >
                            Delete
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))
            )}
          </Stack>
        ) : (
          // Desktop Table Layout
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ minWidth: 60, fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 80, fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 80, fontWeight: 600 }}>URLs</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>Clicks</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Registered</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow 
                      key={user.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'grey.50'
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{user.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={user.role === 'admin' ? 'primary' : 'default'}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Active' : 'Inactive'}
                          color={user.is_active ? 'success' : 'error'}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {(user.url_count || 0).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {(user.total_clicks || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {user.is_active ? (
                            <Tooltip title="Deactivate User">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => openConfirmDialog(
                                  'Deactivate User',
                                  `Are you sure you want to deactivate ${user.username}?`,
                                  () => handleUserAction(user.id, 'deactivate')
                                )}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'warning.light',
                                    color: 'warning.contrastText'
                                  }
                                }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate User">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openConfirmDialog(
                                  'Activate User',
                                  `Are you sure you want to activate ${user.username}?`,
                                  () => handleUserAction(user.id, 'activate')
                                )}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'success.light',
                                    color: 'success.contrastText'
                                  }
                                }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {user.role !== 'admin' && (
                            <Tooltip title="Delete User">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openConfirmDialog(
                                  'Delete User',
                                  `Are you sure you want to permanently delete ${user.username}? This action cannot be undone.`,
                                  () => handleUserAction(user.id, 'delete')
                                )}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'error.light',
                                    color: 'error.contrastText'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Box 
          display="flex" 
          justifyContent="center" 
          mt={{ xs: 3, sm: 4 }}
          sx={{
            '& .MuiTablePagination-root': {
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }
          }}
        >
          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                gap: 1
              }
            }}
          />
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: '90vw', sm: 400 }
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.action}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity="info"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Admin;